import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../lib/i18n";
import { useStepScroll } from "../lib/useStepScroll";
import { Overline } from "../components/ui-vision";

const cx = (...a) => a.filter(Boolean).join(" ");
const spring = { type: "spring", stiffness: 260, damping: 30 };

const HABITATIONS = [
  { k: "renovation", fr: "Rénovation", nl: "Renovatie" },
  { k: "construction", fr: "Nouvelle construction", nl: "Nieuwbouw" },
];
const MENUISERIES = [
  { id: "Fenêtres", fr: "Fenêtres", nl: "Ramen" },
  { id: "Portes d'entrée", fr: "Portes d'entrée", nl: "Voordeuren" },
  { id: "Portes de garage", fr: "Portes de garage", nl: "Garagepoorten" },
  { id: "Volets", fr: "Volets", nl: "Rolluiken" },
  { id: "Moustiquaires", fr: "Moustiquaires", nl: "Horren" },
  { id: "Clôtures", fr: "Clôtures", nl: "Omheiningen" },
];
const MATERIALS = [
  { k: "pvc", fr: "PVC", nl: "PVC" },
  { k: "alu", fr: "Aluminium", nl: "Aluminium" },
  { k: "unknown", fr: "Je ne sais pas", nl: "Ik weet het niet" },
];
const GLAZINGS = [
  { k: "double", fr: "Double vitrage", nl: "Dubbele beglazing" },
  { k: "triple", fr: "Triple vitrage", nl: "Driedubbele beglazing" },
];

const STR = {
  fr: {
    overline: "Devis",
    title: "Demandez votre devis.",
    lead: "Quelques précisions sur votre projet nous permettent de préparer une estimation au plus juste.",
    leadNote: "Répondez aux questions ci-dessous, puis détaillez votre demande.",
    q1: "Type d'habitation ?",
    q2: "Type de menuiseries ?",
    q2sub: "Plusieurs choix sont possibles",
    q3: "Matériaux souhaités ?",
    q4: "Type de vitrage souhaité ?",
    suivant: "Suivant",
    precisions: "Précisions sur votre demande",
    para1: "Afin de vous proposer un devis aussi proche que possible de la réalité, indiquez le nombre de menuiseries souhaitées, leur type ainsi que leurs dimensions approximatives au centimètre près.",
    para2: "Si vous préférez que nous venions prendre les mesures directement sur place, vous pouvez prendre rendez-vous avec Vision+.",
    rdvLink: "Prendre rendez-vous",
    detailsLabel: "Détails de votre demande",
    detailsPh: "Ex. : 4 fenêtres de 120 × 140 cm, 1 porte d'entrée de 95 × 215 cm…",
    nom: "Nom",
    prenom: "Prénom",
    email: "Adresse e-mail",
    phone: "Téléphone",
    cp: "Code postal",
    errNom: "Veuillez indiquer votre nom.",
    errPrenom: "Veuillez indiquer votre prénom.",
    errEmailReq: "Veuillez indiquer votre adresse e-mail.",
    errEmailInvalid: "Veuillez saisir une adresse e-mail valide.",
    submit: "Envoyer ma demande",
    confTitle: "Votre demande a bien été envoyée.",
    confSub: "Nous vous recontacterons dans les meilleurs délais.",
    retour: "Retour",
  },
  nl: {
    overline: "Offerte",
    title: "Vraag uw offerte aan.",
    lead: "Met enkele details over uw project kunnen wij een zo nauwkeurig mogelijke raming opstellen.",
    leadNote: "Beantwoord de onderstaande vragen en beschrijf vervolgens uw aanvraag.",
    q1: "Type woning?",
    q2: "Type schrijnwerk?",
    q2sub: "Meerdere keuzes mogelijk",
    q3: "Gewenste materialen?",
    q4: "Gewenst type beglazing?",
    suivant: "Volgende",
    precisions: "Details van uw aanvraag",
    para1: "Om u een zo realistisch mogelijke offerte te bezorgen, vermeldt u het gewenste aantal schrijnwerken, hun type en hun benaderende afmetingen tot op de centimeter.",
    para2: "Als u liever heeft dat wij de metingen ter plaatse komen opnemen, kunt u een afspraak maken met Vision+.",
    rdvLink: "Afspraak maken",
    detailsLabel: "Details van uw aanvraag",
    detailsPh: "Bijv.: 4 ramen van 120 × 140 cm, 1 voordeur van 95 × 215 cm…",
    nom: "Naam",
    prenom: "Voornaam",
    email: "E-mailadres",
    phone: "Telefoon",
    cp: "Postcode",
    errNom: "Gelieve uw naam in te vullen.",
    errPrenom: "Gelieve uw voornaam in te vullen.",
    errEmailReq: "Gelieve uw e-mailadres in te vullen.",
    errEmailInvalid: "Gelieve een geldig e-mailadres in te vullen.",
    submit: "Mijn aanvraag versturen",
    confTitle: "Uw aanvraag is goed verzonden.",
    confSub: "Wij nemen zo snel mogelijk contact met u op.",
    retour: "Terug",
  },
};

const PH = { nom: "Dupont", prenom: { fr: "Jean", nl: "Jan" }, email: { fr: "jean.dupont@email.com", nl: "jan.janssens@email.com" }, phone: "0470 12 34 56", cp: "1000" };

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

const Choice = ({ label, selected, disabled, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    data-selected={selected ? "true" : "false"}
    className={cx(
      "group rounded-2xl border px-5 py-4 text-left text-base flex items-center justify-between gap-3 transition-colors duration-300",
      selected
        ? "border-accent-yellow bg-accent-yellow/10 text-white"
        : "border-white/12 bg-white/[0.03] text-white/85 hover:border-accent-yellow/50 hover:bg-white/[0.06]",
      disabled && "opacity-25 pointer-events-none"
    )}
  >
    <span>{label}</span>
    {selected && <Check size={16} className="text-accent-yellow shrink-0" />}
  </button>
);

const Section = ({ num, title, subtitle, active, children, testid }) => (
  <div
    data-testid={testid}
    data-active={active ? "true" : "false"}
    aria-disabled={!active}
    inert={!active ? true : undefined}
    className={cx("transition-opacity duration-500", !active && "opacity-40 pointer-events-none select-none")}
  >
    <div className="flex items-baseline gap-3 mb-4">
      <span className="font-display text-sm text-accent-yellow">{`0${num}`}</span>
      <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight leading-tight">{title}</h3>
    </div>
    {subtitle && <p className="text-sm text-white/45 mb-4 -mt-2">{subtitle}</p>}
    {children}
  </div>
);

const Field = ({ label, value, onChange, placeholder, required, error, type = "text", testid }) => (
  <label className="block group">
    <span className="text-xs uppercase tracking-[0.2em] text-white/40 group-focus-within:text-accent-yellow transition-colors duration-300">
      {label}
      {required && <span className="text-accent-yellow ml-1">*</span>}
    </span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className={cx(
        "w-full bg-transparent border-b py-2 mt-1 text-sm text-white outline-none focus:border-accent-yellow transition-colors duration-300 placeholder:text-white/25",
        error ? "border-accent-yellow" : "border-white/20"
      )}
    />
    {error && (
      <span data-testid={`${testid}-error`} className="mt-1.5 block text-xs text-accent-yellow">
        {error}
      </span>
    )}
  </label>
);

export default function Devis() {
  const { lang } = useLang();
  const s = STR[lang];

  const [step, setStep] = useState(1);
  const [habitation, setHabitation] = useState(null);
  const [menuiseries, setMenuiseries] = useState([]);
  const [material, setMaterial] = useState(null);
  const [glazing, setGlazing] = useState(null);

  const [form, setForm] = useState({ nom: "", prenom: "", email: "", phone: "", cp: "", details: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(null);
  useStepScroll(submitted ? "confirm" : `step-${step}`, formRef);

  // Scroll the newly revealed question into view (not the top of the form).
  const q2Ref = useRef(null);
  const q3Ref = useRef(null);
  const q4Ref = useRef(null);
  const scrollToQ = (ref) => {
    const el = ref.current;
    if (!el) return;
    const OFFSET = 120; // leave breathing room below the fixed header
    const lenis = window.__lenis;
    if (lenis && typeof lenis.scrollTo === "function") {
      lenis.scrollTo(el, { offset: -OFFSET, duration: 0.6 });
    } else {
      const top = el.getBoundingClientRect().top + window.pageYOffset - OFFSET;
      window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
    }
  };

  const s2Active = !!habitation;
  const s3Active = s2Active && menuiseries.length > 0;
  const s4Active = s3Active && !!material;
  const canNext = s4Active && !!glazing;

  useEffect(() => {
    if (habitation) scrollToQ(q2Ref);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habitation]);
  // Q2 (menuiseries) is multi-select: intentionally NO auto-scroll on select/deselect.
  useEffect(() => {
    if (material) scrollToQ(q4Ref);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [material]);

  const toggleMenuiserie = (id) =>
    setMenuiseries((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const selectMaterial = (k) => {
    setMaterial(k);
    if (k === "alu" && glazing === "double") setGlazing(null);
  };

  const upd = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.nom.trim()) errs.nom = s.errNom;
    if (!form.prenom.trim()) errs.prenom = s.errPrenom;
    if (!form.email.trim()) errs.email = s.errEmailReq;
    else if (!emailOk(form.email)) errs.email = s.errEmailInvalid;
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/devis`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ habitation, menuiseries, material, glazing, ...form, lang }),
      });
    } catch (err) {
      console.error("Devis submission failed", err);
    }
    setSubmitted(true);
  };

  return (
    <section className="pt-28 md:pt-32 pb-16 md:pb-20 max-w-[1440px] mx-auto px-6 md:px-12" data-testid="devis">
      <div className="max-w-3xl mx-auto space-y-6 lg:space-y-10">
        <div>
          <div className="lg:hidden rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4" data-testid="devis-mobile-title">
            <Overline>{s.title.replace(/\.$/, "")}</Overline>
          </div>
          <div className="hidden lg:block rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4" data-testid="devis-intro-card">
            <Overline>{s.title.replace(/\.$/, "")}</Overline>
          </div>
        </div>

        <div ref={formRef}>
          <div className="rounded-3xl border border-white/10 bg-white/[0.015] p-6 md:p-10 lg:p-12">
            <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-3xl border border-accent-yellow/40 bg-white/[0.03] p-8 md:p-14 text-center"
              data-testid="devis-confirmation"
            >
              <div className="w-14 h-14 rounded-full bg-accent-yellow text-ink flex items-center justify-center mx-auto mb-8">
                <CheckCircle2 size={26} />
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-4">{s.confTitle}</h2>
              <p className="text-white/60 font-light">{s.confSub}</p>
            </motion.div>
          ) : step === 1 ? (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={spring}
              className="space-y-12"
            >
              <Section num={1} title={s.q1} active testid="devis-q1">
                <div className="grid sm:grid-cols-2 gap-3 max-w-2xl">
                  {HABITATIONS.map((h) => (
                    <Choice
                      key={h.k}
                      label={h[lang]}
                      selected={habitation === h.k}
                      onClick={() => setHabitation(h.k)}
                      testid={`devis-habitation-${h.k}`}
                    />
                  ))}
                </div>
              </Section>

              <div ref={q2Ref}>
              <Section num={2} title={s.q2} subtitle={s.q2sub} active={s2Active} testid="devis-q2">
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 max-w-3xl">
                  {MENUISERIES.map((m) => (
                    <Choice
                      key={m.id}
                      label={m[lang]}
                      selected={menuiseries.includes(m.id)}
                      onClick={() => toggleMenuiserie(m.id)}
                      testid={`devis-menuiserie-${m.id}`}
                    />
                  ))}
                </div>
              </Section>
              </div>

              <div ref={q3Ref}>
              <Section num={3} title={s.q3} active={s3Active} testid="devis-q3">
                <div className="grid sm:grid-cols-3 gap-3 max-w-2xl">
                  {MATERIALS.map((mat) => (
                    <Choice
                      key={mat.k}
                      label={mat[lang]}
                      selected={material === mat.k}
                      onClick={() => selectMaterial(mat.k)}
                      testid={`devis-material-${mat.k}`}
                    />
                  ))}
                </div>
              </Section>
              </div>

              <div ref={q4Ref}>
              <Section num={4} title={s.q4} active={s4Active} testid="devis-q4">
                <div className="grid sm:grid-cols-2 gap-3 max-w-2xl">
                  {GLAZINGS.map((g) => {
                    const disabled = g.k === "double" && material === "alu";
                    return (
                      <Choice
                        key={g.k}
                        label={g[lang]}
                        selected={glazing === g.k}
                        disabled={disabled}
                        onClick={() => setGlazing(g.k)}
                        testid={`devis-glazing-${g.k}`}
                      />
                    );
                  })}
                </div>
              </Section>
              </div>

              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => canNext && setStep(2)}
                  disabled={!canNext}
                  data-testid="devis-next"
                  className={cx(
                    "group inline-flex items-center gap-3 px-8 py-4 rounded-xl font-semibold transition-colors duration-300",
                    canNext
                      ? "bg-accent-yellow text-ink hover:bg-white"
                      : "bg-white/[0.06] text-white/35 cursor-not-allowed"
                  )}
                >
                  {s.suivant}
                  <ArrowRight
                    size={18}
                    className={cx("transition-transform duration-300", canNext && "group-hover:translate-x-1")}
                  />
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.form
              key="step2"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={spring}
              onSubmit={submit}
              noValidate
              className="space-y-8"
              data-testid="devis-form"
            >
              <button
                type="button"
                onClick={() => setStep(1)}
                data-testid="devis-back"
                className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-accent-yellow transition-colors duration-300"
              >
                <ArrowLeft size={16} /> {s.retour}
              </button>

              <div>
                <Overline className="mb-4">{s.precisions}</Overline>
                <p className="text-white/60 font-light leading-relaxed max-w-2xl">{s.para1}</p>
                <p className="mt-4 text-white/60 font-light leading-relaxed max-w-2xl">{s.para2}</p>
                <Link
                  to="/rendez-vous"
                  data-testid="devis-rdv-link"
                  className="mt-3 inline-flex items-center gap-2 text-sm text-accent-yellow hover:text-white transition-colors duration-300"
                >
                  {s.rdvLink}
                  <ArrowRight size={15} />
                </Link>
              </div>

              <label className="block group">
                <span className="text-xs uppercase tracking-[0.2em] text-white/40 group-focus-within:text-accent-yellow transition-colors duration-300">
                  {s.detailsLabel}
                </span>
                <textarea
                  rows={4}
                  value={form.details}
                  onChange={upd("details")}
                  data-testid="devis-details"
                  placeholder={s.detailsPh}
                  className="w-full bg-transparent border border-white/15 rounded-2xl px-4 py-3 mt-2 text-sm text-white outline-none focus:border-accent-yellow transition-colors duration-300 resize-none placeholder:text-white/25"
                />
              </label>

              <div className="grid md:grid-cols-2 gap-6">
                <Field label={s.nom} value={form.nom} onChange={upd("nom")} placeholder={PH.nom} required error={errors.nom} testid="devis-nom" />
                <Field label={s.prenom} value={form.prenom} onChange={upd("prenom")} placeholder={PH.prenom[lang]} required error={errors.prenom} testid="devis-prenom" />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <Field label={s.email} type="email" value={form.email} onChange={upd("email")} placeholder={PH.email[lang]} required error={errors.email} testid="devis-email" />
                <Field label={s.phone} type="tel" value={form.phone} onChange={upd("phone")} placeholder={PH.phone} testid="devis-phone" />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <Field label={s.cp} value={form.cp} onChange={upd("cp")} placeholder={PH.cp} testid="devis-cp" />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  data-testid="devis-submit"
                  className="group inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-accent-yellow text-ink font-semibold hover:bg-white transition-colors duration-300"
                >
                  {s.submit}
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </motion.form>
          )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
